// Paginated media feed — round-robin across tags, cursor-based searchPosts pages.
import { filterKind } from './media'
import { searchTagPage, RateLimitedError } from './search'
import type { PreloadTag, PreloadProgress } from './preload'
import type { ContentKind, MediaItem } from '../types'

const PAGE_GAP_MS = 220
const RL_BACKOFF_MS = 6_000
const RL_MAX_RETRIES = 4
const MAX_PAGES_PER_TAG = 40

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

interface TagCursor {
  tag: PreloadTag
  sort: 'top' | 'latest'
  cursor?: string
  pages: number
  done: boolean
}

export interface MediaFeedOpts {
  tags: PreloadTag[]
  kind: ContentKind
  onProgress?: (p: PreloadProgress) => void
  signal?: AbortSignal
}

export class MediaFeed {
  readonly kind: ContentKind
  private readonly tags: TagCursor[]
  private readonly seen = new Set<string>()
  private readonly items: MediaItem[] = []
  private readonly onProgress?: (p: PreloadProgress) => void
  private readonly signal?: AbortSignal
  private tagIdx = 0
  private totalPages = 0
  loadedMs = 0

  constructor(opts: MediaFeedOpts) {
    this.kind = opts.kind
    this.onProgress = opts.onProgress
    this.signal = opts.signal
    this.tags = opts.tags.map((tag) => ({
      tag,
      sort: 'top' as const,
      pages: 0,
      done: false,
    }))
  }

  get queue(): MediaItem[] {
    return this.items
  }

  get count(): number {
    return this.items.length
  }

  get hasMore(): boolean {
    return this.tags.some((t) => !t.done) && this.totalPages < MAX_PAGES_PER_TAG * this.tags.length
  }

  private report(targetMs: number, note?: string) {
    this.onProgress?.({
      loadedMs: this.loadedMs,
      targetMs,
      count: this.items.length,
      fraction: Math.min(1, this.items.length / Math.max(1, Math.ceil(targetMs / 4000))),
      note,
    })
  }

  /** Fetch the next page from the next tag in rotation. Returns new item count. */
  async fetchNextPage(): Promise<number> {
    if (!this.hasMore) return 0
    if (this.signal?.aborted) throw new DOMException('aborted', 'AbortError')

    let added = 0
    const startIdx = this.tagIdx
    // Try each tag once this round.
    for (let n = 0; n < this.tags.length; n++) {
      const st = this.tags[this.tagIdx % this.tags.length]
      this.tagIdx++

      if (st.done) continue

      const batch = await this.fetchTagPage(st)
      added += batch
      if (batch > 0) break
      if (this.tagIdx % this.tags.length === startIdx) break
    }

    return added
  }

  private async fetchTagPage(st: TagCursor): Promise<number> {
    let attempt = 0
    while (attempt <= RL_MAX_RETRIES) {
      if (this.signal?.aborted) throw new DOMException('aborted', 'AbortError')
      try {
        const { items: page, cursor } = await searchTagPage(st.tag.query, st.tag.label, {
          cursor: st.cursor,
          sort: st.sort,
          limit: 25,
        })
        this.totalPages++
        st.pages++

        let added = 0
        for (const m of filterKind(page, this.kind)) {
          if (this.seen.has(m.id)) continue
          this.seen.add(m.id)
          this.items.push(m)
          this.loadedMs += m.worthMs
          added++
        }

        if (st.sort === 'top' && st.pages >= 2 && !cursor) {
          st.sort = 'latest'
          st.cursor = undefined
        } else if (cursor) {
          st.cursor = cursor
        } else {
          if (st.sort === 'top') {
            st.sort = 'latest'
            st.cursor = undefined
          } else {
            st.done = true
          }
        }

        if (st.pages >= MAX_PAGES_PER_TAG) st.done = true
        return added
      } catch (e) {
        if (e instanceof RateLimitedError && attempt < RL_MAX_RETRIES) {
          attempt++
          this.report(0, 'Bluesky rate limit — pausing…')
          await sleep(RL_BACKOFF_MS * attempt)
          continue
        }
        // Skip this tag for now but don't permanently kill it unless many failures.
        if (st.pages === 0 || attempt >= RL_MAX_RETRIES) st.done = true
        return 0
      }
    }
    return 0
  }

  /** Load until we have at least `minItems` tiles (or tags exhaust). */
  async loadUntil(minItems: number, targetMs = minItems * 4000): Promise<MediaItem[]> {
    let idleRounds = 0
    while (this.items.length < minItems && this.hasMore) {
      const added = await this.fetchNextPage()
      this.report(targetMs)
      if (added === 0) {
        idleRounds++
        if (idleRounds >= this.tags.length * 2) break
      } else {
        idleRounds = 0
      }
      await sleep(PAGE_GAP_MS)
    }
    this.report(targetMs)
    return this.items
  }

  /** Continue loading toward a time-based target (game mode). */
  async loadToTarget(targetMs: number, minItems = 20): Promise<MediaItem[]> {
    await this.loadUntil(minItems, targetMs)
    while (this.loadedMs < targetMs && this.hasMore) {
      const added = await this.fetchNextPage()
      this.report(targetMs)
      if (added === 0) break
      await sleep(PAGE_GAP_MS)
    }
    this.report(targetMs)
    return this.items
  }
}